import { AxiosProgressEvent } from 'axios';
import { StateCreator } from 'zustand';

import { QuoteType, TermInsuranceProduct } from '@common/types';
import insureAPIs, {
  suggestionTransform,
  estimateTransform,
  addOnsTransform,
  termPremiumTransform,
  finalizeTransform
} from '@services/apis/insure';
import { accountAPIs } from '@services/apis/insure/account';
import { SessionStorage } from '@services/storage/storage';
import { TERM_QUOTE_STEPS } from '@termquote/constants';
import { getPremium } from '@termquote/utils';
import { dayjs } from '@ui';
import { TERM_INSURANCE_PRODUCTS } from '@utils/constants';

import {
  checkRiderPremiumOverlap,
  getProductDetails,
  getStepProgress
} from './selectors';
import {
  BoundaryDependency,
  PaymentFrequency,
  PolicyVerificationResult,
  Suggestion,
  TermAddOn,
  TermQuoteStoreState,
  UserSliceState
} from './types';

const planInitialValues = {
  fullName: '',
  dob: null,
  gender: undefined,
  tobacco: undefined,
  pincode: undefined,
  existingCustomer: undefined,
  firsTimeBuyer: undefined,
  diabetic: undefined,
  annInc: undefined,
  mExpenses: undefined,
  product: '',
  cover: 0,
  tillAge: 65,
  paymentFreq: 'Yearly' as PaymentFrequency,
  payOption: '',
  education: undefined,
  salaried: undefined,
  mobile: '',
  isNRI: undefined,
  nriCountry: '',
  gstWaiver: undefined,
  otp_id: undefined,
  verified_country_code: undefined,
  verified_phone_num: undefined,
  policy_verification_cooldown_until: undefined,
  old_policy_number: undefined
};

const INITIAL_PREMIUM = {
  basePlan: {
    base: 0,
    tax: 0
  },
  adhb: {
    base: 0,
    tax: 0
  },
  acb: {
    base: 0,
    tax: 0
  },
  ci: {
    base: 0,
    tax: 0
  },
  total: {
    base: 0,
    tax: 0
  },
  secondYearPremium: {
    baseWithTax: 0,
    base: 0,
    tax: 0
  },
  firstYearDiscount: 0
};

const POLICY_VERIFICATION_COOLDOWN_MS = 30_000;

export const userSlice: StateCreator<
  TermQuoteStoreState,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  UserSliceState
> = (set, get) => ({
  isNRIEnabled: false,
  quote: QuoteType.Normal,
  plan: planInitialValues,
  derived: {
    age: 18, // set to min age for now
    payOptionItem: undefined
  },
  premium: INITIAL_PREMIUM,
  loadingPremium: false,
  premiumApiProgress: 0,
  premiumFetched: false,
  /**
   * Step to keep track of the current page user is on
   */
  step: 1,
  /**
   *  Derived property, updated whenever step changes
   */
  stepProgress: 0,
  /**
   * loader state for suggestion and estimate APIs
   */
  loadingSuggestion: false,
  suggestionFetched: false,
  /**
   * Suggestion API error
   */
  suggestionError: '',
  loadingEstimate: false,
  estimateFetched: false,
  /**
   * Suggestion for the user
   */
  suggestion: null,
  recommendedProduct: null,
  /**
   * Store user about details
   * @param aboutDetails
   */
  addOns: [],
  loadingAddOns: false,
  currentAddOn: '',
  policy: null,
  premiumOverlap: false,
  disableWopMaxLife: false,
  planFormValid: false,
  policyVerification: {
    oldPolicyNumber: undefined,
    otpId: undefined,
    countryCode: undefined,
    phoneNum: undefined,
    cooldownUntil: undefined
  },
  setPlanFormValid: (isValid) => {
    set((state) => {
      state.planFormValid = isValid;
    });
  },
  oldPolicyNumber: undefined,
  setAboutUserFields: async (aboutDetails) => {
    set((state) => {
      state.plan = {
        ...state.plan,
        ...aboutDetails
      };

      // set the payment frequency to yearly for NRI with GST waiver
      if (aboutDetails.isNRI && aboutDetails.gstWaiver) {
        state.plan.paymentFreq = 'Yearly';
      }

      // unset NRI related fields is user is not NRI
      if (!aboutDetails.isNRI) {
        state.plan.gstWaiver = false;
        state.plan.nriCountry = undefined;
      }

      // store the age from dob and avoid recalculating from other components
      state.derived.age = dayjs().diff(
        dayjs(aboutDetails.dob).format('YYYY-MM-DD'),
        'years'
      );
    });

    await get().refetchProductBoundaries(BoundaryDependency.NRI);
    await get().fetchProductFeatures([get().plan.product]);
  },
  /**
   * Store user life style details and trigger, At this point quote form minimum required fields are filled
   * we can fetch the cover suggestion
   * @param lifeStyleDetails
   */
  setLifeStyleUserFields: async (lifeStyleDetails) => {
    set((state) => {
      state.plan = { ...state.plan, ...lifeStyleDetails };
      // if store has policy set clear it
      state.policy = null;
    });

    try {
      await get().getCoverSuggestion();
    } catch (err) {
      // handle any reset logic if we have any for this step
    }
  },
  /**
   * clear suggestion
   */
  clearSuggestion: () => {
    set((state) => {
      state.suggestion = null;
      state.suggestionFetched = false;
      state.estimateFetched = false;
      state.premiumFetched = false;
      state.loadingEstimate = false;
      state.loadingEstimate = false;
      state.loadingPremium = false;
      state.suggestionError = '';
      state.premium = INITIAL_PREMIUM;
      state.plan.payOption = '';
    });
  },
  /**
   * Get cover suggestion via API
   */
  getCoverSuggestion: async () => {
    const plan = get().plan;
    const quote = get().quote;

    set((state) => {
      // clear existing suggestion
      state.suggestion = null;
      state.loadingSuggestion = true;
      // Reset addOns, payoptions whenever suggestion changes in order to refetch them later
      state.addOns = [];
      state.paymentTermOptions = [];
      // Reset error message if any
      state.suggestionError = '';
    });

    try {
      const suggestionResponse = await insureAPIs.getTermSuggestions(
        suggestionTransform(plan, quote)
      );

      const { maxAllowed, coverTill, suggestedCover, premium } =
        suggestionResponse.data;

      const suggestion: Suggestion = {
        maxAllowed,
        suggestedCover,
        coverTill,
        recommended: premium.recommended,
        products: premium.products,
        betterOption: premium.betterOption
      };

      set((state) => {
        // reset loading state
        state.loadingSuggestion = false;
        // set suggestion in store
        state.suggestion = suggestion;
        // update users cover to recommended value if not already set
        state.plan = {
          ...plan,
          cover: suggestedCover,
          tillAge: suggestion.coverTill,
          paymentFreq: 'Yearly'
        };
        // update the premium if product is already set
        state.premium = getPremium(plan.product, suggestion);
        state.suggestionFetched = true;
      });

      /**
       * After receiving the suggestion fetch boundaries for all products in the suggestion
       * The boundaries contain data points which powers the components from this point onwards
       */
      const productKeys = premium.products
        .filter((product) => {
          return product.available && product.success;
        })
        .map((product) => product.key);
      if (productKeys.length) {
        await get().fetchProductBoundaries(productKeys);
        await get().fetchProductFeatures(productKeys);
      }
    } catch (err) {
      set((state) => {
        state.loadingSuggestion = false;
        state.suggestionError =
          'An error occurred while retrieving recommended cover! Please check again later.';
      });
      // rethrow error so that any store functions using getSuggestion can reset the state
      throw err;
    }
  },
  /**
   * Update the user's cover,
   * whenever this happens we need to get the latest estimate
   * @param latestCover
   */
  updateUserCover: async (latestCover) => {
    const plan = get().plan;
    const prevCover = plan.cover;
    const selectedVariant = plan.productVariant;
    const loadingEstimate = get().loadingEstimate;
    set((state) => {
      state.plan.cover = latestCover;
      state.addOns = [];
      state.paymentTermOptions = [];
    });
    try {
      // get latest estimate since the cover has changed
      // don't get estimates in case if a variant of the product is selected  instead refetch boundaries
      if (!selectedVariant && !loadingEstimate) {
        await get().getEstimate();
      } else {
        await get().fetchProductBoundaries([plan.product]);
      }
    } catch (err) {
      // reset state since the api has failed
      set((state) => {
        state.plan.cover = prevCover;
      });
    }
  },
  /**
   * Update the user's till age,
   * whenever this happens we need to get the latest estimate
   */
  updateUserCoverTill: async (latestCoverTill) => {
    const plan = get().plan;
    const prevTillAge = plan.tillAge;
    const selectedVariant = plan.productVariant;
    set((state) => {
      state.plan.tillAge = latestCoverTill;
      state.addOns = [];
      state.paymentTermOptions = [];
    });
    try {
      // get latest estimate since cover till has changed
      // don't get estimates in case if a variant of the product is selected instead refetch boundaries
      if (!selectedVariant) {
        await get().getEstimate();
      } else {
        /**
         * Avoid calling this for now as there is no dependency on till age on boundaries as of now.
         * reenable if boundaries become dependent on till age for variants
         */
        // await get().fetchProductBoundaries([plan.product]);
      }
    } catch (err) {
      // reset state since api has failed
      set((state) => {
        state.plan.tillAge = prevTillAge;
      });
    }
  },
  /**
   * Update the user's payment frequency,
   * whenever this happens we need to get the latest estimate
   */
  updateUserPaymentFreq: async (latestPaymentFreq) => {
    const prevPaymentFreq = get().plan.paymentFreq;
    const step = get().step;
    set((state) => {
      state.plan.paymentFreq = latestPaymentFreq;
    });
    try {
      // get the latest estimate since payment frequency has changed
      await get().getEstimate(false, true);
      // get the payment options with the new payment frequency
      if (step === TERM_QUOTE_STEPS.PAYMENT_TERM_OPTIONS) {
        get().fetchPaymentTermOptions();
      } else {
        get().clearPaymentTermOptions();
      }
    } catch (err) {
      set((state) => {
        state.plan.paymentFreq = prevPaymentFreq;
      });
    }
  },
  /**
   * Get the latest Estimates via API
   */
  getEstimate: async (premiumChangeInfo = true, retainFeatures = false) => {
    const plan = get().plan;
    const step = get().step;
    const suggestion = get().suggestion;
    const quote = get().quote;
    const prevPremium = get().premium;

    set((state) => {
      state.loadingEstimate = true;
      // Reset addOns whenever estimate changes in order to refetch addons later
      state.addOns = [];
    });

    try {
      const premium = await insureAPIs.getTermEstimate(
        estimateTransform(plan, quote)
      );
      if (!suggestion) {
        // Suggestion is reset and fetched on submitting the lifestyle form.
        // If the user:
        // 1. changes the cover from the suggestion page, getEstimate action is triggered.
        // 2. navigates back and submits the lifestyle form
        // the suggestion could be null.
        set((state) => {
          state.loadingEstimate = false;
        });

        return;
      }

      const { products, recommended, betterOption } = premium.data;
      // Modify existing suggestion with latest values
      const newSuggestion: Suggestion = {
        ...suggestion!,
        products,
        recommended,
        betterOption: betterOption ?? undefined
      };
      // Get premium from latest suggestion
      const premiumObj = getPremium(
        plan.product ? plan.product : newSuggestion.recommended,
        newSuggestion
      );
      // toggle premium change model after cover and duration step if there is a change in premium
      if (
        step > TERM_QUOTE_STEPS.COVER_AND_DURATION &&
        premiumChangeInfo &&
        premiumObj.basePlan.base !== prevPremium.basePlan.base
      ) {
        get().showPremiumChangeModal(
          premiumObj.basePlan.base + premiumObj.basePlan.tax
        );
      }

      // get boundaries for available products
      if (products.length) {
        await get().fetchProductBoundaries(
          products
            .filter((item) => item.available && item.success)
            .map((item) => item.key),
          retainFeatures
        );
        await get().fetchProductFeatures([plan.product]);
      }

      set((state) => {
        if (state.suggestion) {
          state.suggestion = newSuggestion;
        }
        state.loadingEstimate = false;
        state.premium = premiumObj;
        state.estimateFetched = true;
      });
    } catch (err) {
      set((state) => {
        state.loadingEstimate = false;
      });

      // rethrow error so that any store functions using getEstimate can reset the state
      throw err;
    }
  },
  /**
   * Update the user's basic details,
   * whenever this happens we need to get the latest estimate or reset and go back to plans page
   */
  setUserDetails: async (latestUserDetails) => {
    const prevPlan = get().plan;
    const prevAge = get().derived.age;
    const product = prevPlan.product;

    set((state) => {
      state.plan = {
        ...state.plan,
        ...latestUserDetails
      };
      // reset payment frequency to 'Yearly' if user choses gst waiver
      if (latestUserDetails.gstWaiver) {
        state.plan.paymentFreq = 'Yearly';
      }
      // update the age in store
      state.derived.age = dayjs().diff(
        dayjs(latestUserDetails.dob).format('YYYY-MM-DD'),
        'years'
      );
      state.addOns = [];
      state.paymentTermOptions = [];
    });
    const step = get().step;
    try {
      // fetch latest estimate since user details has changed
      await get().getEstimate();
      // if user has selected the product already refetch the boundaries and features
      if (product) {
        await get().refetchProductBoundaries(BoundaryDependency.NRI);
        await get().fetchProductFeatures([get().plan.product]);
      }
      // if we are on payment term options page refetch the latest options
      if (step === TERM_QUOTE_STEPS.PAYMENT_TERM_OPTIONS) {
        get().fetchPaymentTermOptions();
      }
    } catch (err) {
      set((state) => {
        state.plan = prevPlan;
        state.derived.age = prevAge;
      });
    }
  },
  /**
   * Update the user's cover and till age using form containing both the fields,
   * whenever this happens we need to get the latest estimate or go back to all plans page
   */
  setPlanDetails: async (latestPlanDetails) => {
    const prevPlan = get().plan;
    const product = prevPlan.product;

    set((state) => {
      state.plan.cover = latestPlanDetails.cover;
      state.plan.tillAge = latestPlanDetails.tillAge;
      state.plan.paymentFreq = latestPlanDetails.paymentFreq;
      state.plan.payOption = '';
      state.paymentTermOptions = [];
      state.addOns = [];
      state.premiumFetched = false;
    });

    try {
      // refetch estimate since plan details have changed
      await get().getEstimate();
      // if user has selected the product already refetch the features
      if (product) {
        await get().fetchProductFeatures([prevPlan.product]);
      }
    } catch (err) {
      set((state) => {
        state.plan.cover = prevPlan.cover;
        state.plan.tillAge = prevPlan.tillAge;
      });
    }
  },
  // set the current step
  setStep: (latestStep) => {
    /**
     * To handle browser back button click from TERM_QUOTE_STEPS.COVER_AND_DURATION
     * clearing the suggestion to avoid staying in same page.
     */
    if (latestStep === TERM_QUOTE_STEPS.LIFESTYLE_USER) {
      get().clearSuggestion();
    }
    /**
     * Whenever step changes clear the premium change details
     * so that same toast/modal is not shown again
     */
    get().hidePremiumChangeModal();
    set((state) => {
      state.step = latestStep;
      state.stepProgress = getStepProgress(latestStep, get().quote);
    });
  },
  setUserProduct: (latestProduct) => {
    const quote = get().quote;
    const premium = get().premium;
    const suggestion = get().suggestion;
    /**
     * whenever the selected product changes, replace
     * the premium object with the premium of the selected product
     */
    let premiumObj = premium;
    if (suggestion) {
      premiumObj = getPremium(latestProduct, suggestion);
    }
    set((state) => {
      state.plan.product = latestProduct;
      state.premium = premiumObj;
      // reset addons and payment options
      state.paymentTermOptions = [];
      state.addOns = [];
    });
    // fetch the product features when user selects a valid product in case of normal quote
    if (latestProduct !== '' && quote === QuoteType.Normal) {
      get().fetchProductFeatures([latestProduct]);
    }
  },
  // Set/clear the selected payment option in store
  setUserPayOption: async (latestPayOption) => {
    set((state) => {
      if (latestPayOption) {
        state.plan.payOption = latestPayOption.key;
        state.derived.payOptionItem = latestPayOption;
      } else {
        state.plan.payOption = '';
        state.derived.payOptionItem = undefined;
      }
      // reset addons so that it can be re-fetched
      state.addOns = [];
    });
    // fetch the latest premium after addons are retrieved
    await get().getPremium();
  },
  // set the product and quote type as fast
  setFastQuote: async (latestProduct) => {
    const prevProduct = get().plan.product;
    const productDetails = getProductDetails(latestProduct);
    set((state) => {
      state.quote = QuoteType.Fast;
      state.plan.product = latestProduct;
      state.suggestionFetched = false;
      state.premiumFetched = false;
      state.estimateFetched = false;
      state.isNRIEnabled = [
        TERM_INSURANCE_PRODUCTS.I_PROTECT_SMART,
        TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_SUPER,
        TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_SUPREME,
        TERM_INSURANCE_PRODUCTS.CLICK_2_PROTECT_SUPREME_PLUS
      ].includes(latestProduct as TermInsuranceProduct);
    });
    // Fetch the product boundaries for the fast quote product
    if ((latestProduct && latestProduct !== prevProduct) || !productDetails) {
      await get().fetchProductBoundaries([latestProduct]);
      await get().fetchProductFeatures([latestProduct]);
    }
  },
  getAddOnsForPlan: async () => {
    /**
     * Whenever a new set of addOns are fetched we need to get the latest premium
     * so enable the premium loader too at this point.
     */
    const currentAddOns = get().addOns;
    const plan = get().plan;
    if (currentAddOns.length === 0) {
      set((state) => {
        state.loadingAddOns = true;
        state.loadingPremium = true;
        state.addOns = [];
      });

      try {
        const response = await insureAPIs.getAddOns(addOnsTransform(plan));
        const addOns = response.data.map((addOn, index) => {
          // for free addons set included as true
          if (addOn.isFree) {
            addOn.included = addOn.isEligible && !addOn.isOptional;
          } else {
            addOn.included = false;
          }
          // select default values for variant selection
          if (Array.isArray(addOn.variants) && addOn.variants?.length) {
            addOn.selectedVariant = { variant: addOn.variants[0].type, tpd: false };
          }

          // set default values for user selected options within addOn
          addOn.cover = 0;
          // mark the first addOn as seen so that it will show up on TermSummaryBox
          addOn.seen = index === 0;
          addOn.additionalPremium = { base: 0, tax: 0 };
          return addOn;
        });
        set((state) => {
          state.loadingAddOns = false;
          state.addOns = addOns;
          state.currentAddOn = response.data[0].key;
        });
      } catch (err) {
        set((state) => {
          state.loadingAddOns = false;
        });
      }
    }
    // fetch the latest premium after addons are retrieved
    get().getPremium();
  },
  // get the latest premium
  getPremium: async () => {
    set((state) => {
      state.loadingPremium = true;
      state.premiumApiProgress = 0;
    });

    const addOns = get().addOns;
    const plan = get().plan;
    const currentAddOn = get().currentAddOn;

    try {
      const response = await insureAPIs.getTermPremium(
        termPremiumTransform(plan, addOns, currentAddOn),
        // Track upload progress
        (progressEvent: AxiosProgressEvent) => {
          set((state) => {
            // For upload, we'll use 0-50% of our total progress
            const uploadPercentage = Math.round(
              (progressEvent.loaded * 50) / (progressEvent.total ?? 100)
            );
            state.premiumApiProgress = uploadPercentage;
          });
        },
        // Add download progress tracking
        (progressEvent: AxiosProgressEvent) => {
          set((state) => {
            // For download, we'll use 50-100% of our total progress
            const downloadPercentage =
              50 +
              Math.round((progressEvent.loaded * 50) / (progressEvent.total ?? 100));
            state.premiumApiProgress = downloadPercentage;
          });
        }
      );

      const latestPremium = response.data;

      /**
       * Modify the current addons list with latest premium
       */
      const addOnsWithPremium = addOns.map((addOn) => {
        const addOnInResponse = response.data.addOns.find(
          (aon) => aon.key === addOn.key
        );
        return {
          ...addOn,
          isEligible: addOnInResponse?.isEligible ?? addOn.isEligible,
          reason: addOnInResponse?.reason ?? addOn.reason ?? '',
          additionalPremium: response.data[addOn.key],
          desc: addOnInResponse?.desc,
          policyTerm: addOnInResponse?.policyTerm,
          variants: addOnInResponse?.variants
        };
      });
      set((state) => {
        state.loadingPremium = false;
        state.premium = latestPremium;
        state.addOns = addOnsWithPremium;
        // check if there is a premium overlap
        state.premiumOverlap = checkRiderPremiumOverlap(
          latestPremium,
          addOnsWithPremium
        );
        state.premiumFetched = true;
      });
    } catch (err) {
      set((state) => {
        state.loadingPremium = false;
      });
      // rethrow error so that any store functions using getPremium can reset the state
      throw err;
    }
  },
  // include/exclude an addon from the plan
  toggleAddOn: async (addOnKey, included) => {
    const step = get().step;
    const currentAddOn = get().currentAddOn;
    const currentAddOns = get().addOns;
    const modified = currentAddOns.map((ao) => {
      /**
       * if user is on the current addon and and step is less than summary don't clear the cover
       * otherwise clear the cover as well
       * But if its a product where base premium includes paid addons, we need to clear cover to reset base premium
       */
      const modifiedCover =
        step < TERM_QUOTE_STEPS.SUMMARY &&
        currentAddOn === ao.key &&
        !ao.inclusiveInBasePremium
          ? ao.cover
          : 0;
      if (ao.key === addOnKey) {
        return { ...ao, included, cover: !included ? modifiedCover : ao.cover };
      }
      return { ...ao };
    });
    set((state) => {
      state.addOns = modified;
    });
    // whenever an addon is added or removed refetch the premium
    try {
      await get().getPremium();
    } catch (err) {
      // Reset addOns to previous state if premium api fails
      set((state) => {
        state.addOns = currentAddOns;
      });
    }
  },
  // Change addon's cover or variant
  modifyAddOnCover: async (addOnKey, cover, variant) => {
    const currentAddOns = get().addOns;
    // find the index of the addon to update the array
    const index = currentAddOns.findIndex((item) => item.key === addOnKey);
    // make sure add on is present in the list of addons
    if (index !== -1) {
      const prevCover = currentAddOns[index].cover;
      const prevIncludedState = currentAddOns[index].included;
      const prevVariant = currentAddOns[index].selectedVariant;
      // update cover and variant
      set((state) => {
        state.addOns[index].cover = cover;
        if (cover === 0) {
          state.addOns[index].included = false;
          // If user selects a cover and premium is included in base premium - consider it as included
          // Except for CI Rider - breakup available
        } else if (state.addOns[index].inclusiveInBasePremium) {
          state.addOns[index].included = true;
        }
        if (variant) {
          state.addOns[index].selectedVariant = variant;
        }
      });

      // if there is any change in cover or variant options refetch the premium
      if (
        prevCover !== cover ||
        prevVariant?.variant !== variant?.variant ||
        prevVariant?.tpd !== variant?.tpd
      ) {
        try {
          await get().getPremium();
        } catch (err) {
          // reset the changes if API call fails
          set((state) => {
            // Re-lookup by key: the addOns array may have been replaced during the
            // await above, making the previously captured index stale/out-of-bounds.
            const currentIndex = state.addOns.findIndex(
              (item) => item.key === addOnKey
            );
            if (currentIndex === -1) return;
            state.addOns[currentIndex].cover = prevCover;
            // If base premium includes paid addons, we include it while setting cover - so reset it to its previous state on error
            if (state.addOns[currentIndex].inclusiveInBasePremium) {
              state.addOns[currentIndex].included = prevIncludedState;
            }
            if (variant) {
              state.addOns[currentIndex].selectedVariant = prevVariant;
            }
          });
        }
      }
    }
  },
  setCurrentAddOn: (addOnKey) => {
    set((state) => {
      state.currentAddOn = addOnKey;
    });
  },
  // reset the currentAddon to the first one in the list
  resetCurrentAddOn: () => {
    set((state) => {
      state.currentAddOn = state.addOns[0].key;
    });
  },
  resetAddOnPremium: async (addOn) => {
    const currentAddOns = get().addOns;
    if (addOn && !addOn.included && !addOn.isFree && addOn.isEligible) {
      const modified = currentAddOns.map((ao) => {
        if (ao.key === addOn.key) {
          return { ...ao, cover: 0 };
        }
        return { ...ao };
      });
      set((state) => {
        state.addOns = modified;
      });
      try {
        await get().getPremium();
      } catch (err) {
        // handle reset logic if any
      }
    }
  },
  // navigate to the next addon
  selectNextAddOn: async () => {
    const currAddOns = get().addOns;
    const currAddOn = get().currentAddOn;
    const currAddOnIndex = currAddOns.findIndex((item) => item.key === currAddOn);
    let next: TermAddOn | null = null;
    if (currAddOns[currAddOnIndex + 1]) {
      next = { ...currAddOns[currAddOnIndex + 1] };
      next.seen = true;
    }
    set((state) => {
      state.currentAddOn = next ? next.key : state.currentAddOn;
      if (next) {
        state.addOns[currAddOnIndex + 1] = next;
      }
    });

    // incase the current addon was not included reset the cover and refetch premium
    await get().resetAddOnPremium(currAddOns[currAddOnIndex]);
  },
  // navigate to the prev addon
  selectPrevAddOn: async () => {
    const currAddOns = get().addOns;
    const currAddOn = get().currentAddOn;
    const currAddOnIndex = currAddOns.findIndex((item) => item.key === currAddOn);
    let prev: TermAddOn | null = null;
    if (currAddOns[currAddOnIndex - 1]) {
      prev = currAddOns[currAddOnIndex - 1];
    }
    set((state) => {
      state.currentAddOn = prev ? prev.key : state.currentAddOn;
    });

    // in case the current addon was not included reset the cover and refetch premium
    await get().resetAddOnPremium(currAddOns[currAddOnIndex]);
  },
  // finalize the quote and generate a policyID
  finalizeTermQuote: async (user) => {
    const plan = get().plan;
    const addOns = get().addOns;
    const otpId = get().policyVerification?.otpId;

    const response = await insureAPIs.finalizeTermQuote(
      finalizeTransform(plan, addOns, user, otpId)
    );
    const policyId = response.data.policyID;
    if (policyId) {
      set((state) => {
        state.plan.mobile = user.mobile ?? '';
        state.policy = response.data;
      });
      // On quote finalize and policyId creation, remove team from session storage
      // Not necessary to check for existence before removal,
      // if policyID is created, the flow is complete and we remove team since it is saved in db (if visited via team link)
      SessionStorage?.remove('team');
    }
  },
  resetPlan: () => {
    set((state) => {
      state.plan = planInitialValues;
    });
  },
  setWopStatusForMaxLife: (value: boolean) => {
    set((state) => {
      state.disableWopMaxLife = value;
    });
  },
  setUserProductVariant: (variant?: string) => {
    set((state) => {
      state.plan.productVariant = variant;
      // Reset addOns, payoptions whenever variant changes in order to refetch them
      state.addOns = [];
      state.paymentTermOptions = [];
    });
  },
  setPolicyVerificationCooldown: (cooldownUntil?: number) => {
    set((state) => {
      state.policyVerification = {
        ...state.policyVerification,
        cooldownUntil
      };
    });
  },
  // Send policy OTP for verification
  sendPolicyOTP: async (
    policyNumber: string,
    turnstileToken: string
  ): Promise<PolicyVerificationResult> => {
    try {
      const response = await accountAPIs.sendPolicyOTP({
        policy_number: policyNumber,
        turnstile_token: turnstileToken
      });
      if (response.success) {
        const cooldownUntil = Date.now() + POLICY_VERIFICATION_COOLDOWN_MS;
        set((state) => {
          state.policyVerification = {
            cooldownUntil,
            oldPolicyNumber: policyNumber
          };
        });
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: 'Failed to send OTP. Please try again.'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'An error occurred while sending OTP'
      };
    }
  },
  // Verify policy OTP and store verification details
  verifyPolicyOTP: async (otp: string): Promise<PolicyVerificationResult> => {
    try {
      const policyNumber = get().policyVerification?.oldPolicyNumber;
      if (!policyNumber) {
        return {
          success: false,
          message: 'Failed to verify the OTP, Please refresh the page.'
        };
      }
      const response = await accountAPIs.verifyPolicyOTP({
        policy_number: policyNumber,
        otp: otp
      });
      if (response.success) {
        set((state) => {
          state.policyVerification = {
            oldPolicyNumber: policyNumber,
            otpId: response.data.otp_id,
            countryCode: response.data.country_code,
            phoneNum: response.data.phone_num,
            cooldownUntil: undefined
          };
        });
        return {
          success: true,
          message: 'Verified your policy successfully.'
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Invalid OTP. Please try again.'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'An error occurred while verifying OTP'
      };
    }
  },
  clearPolicy: () => {
    set((state) => {
      state.policy = null;
    });
  }
});
